import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  DataTable,
  StatusBadge,
  Badge,
  Button,
  Input,
  Select
} from '@besiktning/ui'
import { Search, Eye } from 'lucide-react'
import { garageApi, formatDateTime, formatDistance } from '@besiktning/ui'
import type { InspectionRequest } from '@besiktning/ui'
import { t } from '../i18n'

export default function Requests() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['requests', statusFilter],
    queryFn: () => garageApi.getRequests({ status: statusFilter || undefined }),
  })

  const filteredRequests = requests.filter((request: InspectionRequest) => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      request.vehicle?.reg_number?.toLowerCase().includes(searchLower) ||
      request.vehicle?.make?.toLowerCase().includes(searchLower) ||
      request.kommun?.toLowerCase().includes(searchLower)
    )
  })

  const columns = [
    {
      key: 'vehicle',
      header: t('requests.colVehicle'),
      render: (request: InspectionRequest) => (
        <div>
          <p className="font-medium">
            {request.vehicle?.make} {request.vehicle?.model}
          </p>
          <p className="text-sm text-gray-500">{request.vehicle?.reg_number}</p>
        </div>
      ),
    },
    {
      key: 'location',
      header: t('requests.colLocation'),
      render: (request: InspectionRequest) => (
        <div>
          <p className="text-sm">{request.kommun}</p>
          {request.latitude && request.longitude && (
            <p className="text-xs text-gray-500">
              {formatDistance(
                calculateDistanceFromGarage(request.latitude, request.longitude)
              )}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'issues',
      header: t('requests.colIssues'),
      render: (request: InspectionRequest) => (
        <div className="text-sm">
          <span>{t('requests.issuesCount', { n: request.inspection_issues?.length || 0 })}</span>
          {request.extra_services && request.extra_services.length > 0 && (
            <Badge variant="info" className="ml-2">
              {t('requests.extrasCount', { n: request.extra_services.length })}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'quotes',
      header: t('requests.colQuotes'),
      render: (request: InspectionRequest) => (
        <span className="text-sm">
          {t('requests.quotesCount', { n: request.quotes?.length || 0 })}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('requests.colStatus'),
      render: (request: InspectionRequest) => (
        <div className="flex items-center gap-2">
          <StatusBadge status={request.status} />
          {request.cancelled && <Badge variant="danger">{t('requests.cancelled')}</Badge>}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: t('requests.colReceived'),
      render: (request: InspectionRequest) => (
        <span className="text-sm text-gray-500">
          {formatDateTime(request.created_at)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (request: InspectionRequest) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/requests/${request.id}`)}
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ]

  // Mock function - replace with actual garage coordinates
  function calculateDistanceFromGarage(_lat: number, _lng: number): number {
    return Math.random() * 20 + 1
  }

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('requests.title')}</h1>
          <p className="text-gray-600">
            {t('requests.subtitle')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder={t('requests.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-4 w-4 text-gray-400" />}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: '', label: t('requests.statusAll') },
                  { value: 'open', label: t('requests.statusOpen') },
                  { value: 'booked', label: t('requests.statusBooked') },
                  { value: 'closed', label: t('requests.statusClosed') },
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('requests.tableTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filteredRequests}
            keyExtractor={(request) => request.id}
            isLoading={isLoading}
            onRowClick={(request) => navigate(`/requests/${request.id}`)}
            emptyMessage={t('requests.emptyMessage')}
          />
        </CardContent>
      </Card>
    </div>
  )
}
